/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { ArticlesCollectionModule, getModuleLayoutConfig } from '@coursera-web/cds-labs';

import type { ConsumerPathwayCareerAcademyPageDataContextType } from 'bundles/unified-career-academy/contexts/ConsumerPathwayCareerAcademyPageDataContext';
import type { ConsumerPathwayRolePageDataContextType } from 'bundles/unified-career-academy/contexts/ConsumerPathwayRolePageDataContext';
import { useEventingData } from 'bundles/unified-career-academy/contexts/EventingContext';
import { useEstimatedReadTimesData } from 'bundles/unified-common/hooks/useEstimatedReadTimeData';

import _t from 'i18n!nls/career-academy';

type ArticlesCollectionModuleProps = React.ComponentProps<typeof ArticlesCollectionModule>;
type ArticlesCollectionModuleCollectionSet = ArticlesCollectionModuleProps['articleCollectionSets'][number];
type Article = ArticlesCollectionModuleCollectionSet['articles'][number];

type Props = {
  pageData: ConsumerPathwayRolePageDataContextType | ConsumerPathwayCareerAcademyPageDataContextType;
  removeTopPadding?: boolean;
};

function ArticlesCollectionModuleRenderer({ pageData, removeTopPadding = true }: Props) {
  const { articles } = pageData?.contentfulContent ?? {};
  const eventingData = useEventingData();

  // Make a deduped list of possible slugs for the nostos query
  const slugs = new Set<string>();
  articles?.articleCollectionSetsCollection?.items?.forEach((acsc) => {
    acsc?.articlesCollection?.items?.forEach((a) => {
      if (a?.slug) {
        slugs.add(a?.slug);
      }
    });
  });

  const { loading, results } = useEstimatedReadTimesData({ slugs: Array.from(slugs) });

  const articleCollectionSets: ArticlesCollectionModuleProps['articleCollectionSets'] | undefined =
    articles?.articleCollectionSetsCollection?.items.map<ArticlesCollectionModuleCollectionSet>((articleSet) => ({
      id: articleSet?.sys.id ?? '',
      filter: articleSet?.filter ?? '',
      articles:
        articleSet?.articlesCollection?.items
          .filter((v) => !!v)
          .map<Article>((article) => {
            let articleReadingTime;
            if (!loading && results && article.slug && results[article.slug]) {
              // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              articleReadingTime = results[article.slug].estimatedReadTime;
            }

            return {
              id: article.sys.id ?? '',
              slug: article.slug ?? '',
              title: article.title ?? '',
              description: article.description ?? '',
              publishedDate: article.date,
              estimatedReadingTime: articleReadingTime
                ? _t('#{readingTime} min read', { readingTime: Math.ceil(articleReadingTime) })
                : undefined,
              eventingData,
              metadata: [],
            };
          }) ?? [],
    }));

  if (!articles || !articleCollectionSets?.length) {
    // Without these fields, the content model is nonsensical
    return null;
  }

  const castArticleCollectionProps = (): ArticlesCollectionModuleProps => {
    return {
      heading: articles?.heading ?? '',
      description: articles?.description ?? undefined,
      articleCollectionSets,
    };
  };

  return (
    <ArticlesCollectionModule
      {...getModuleLayoutConfig(articles)}
      {...castArticleCollectionProps()}
      removeTopPadding={removeTopPadding}
    />
  );
}

export default ArticlesCollectionModuleRenderer;
